import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Bookings = () => {
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    firstName: '',
    surname: '',
    phone: '',
    email: '',
    date: '',
    time: '',
    duration: '',
    location: '',
    incallOutcall: '',
    preferences: ''
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-populate form from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const city = params.get('city');
    const dates = params.get('dates');
    const tourLocation = params.get('location');

    if (city || dates || tourLocation) {
      let preferencesText = '';
      if (city) preferencesText += `Tour City: ${city}\n`;
      if (dates) preferencesText += `Tour Dates: ${dates}\n`;
      
      // Extract incall/outcall from location
      let incallOutcallValue = '';
      if (tourLocation) {
        if (tourLocation.toLowerCase().includes('incall')) {
          incallOutcallValue = 'Incall';
        } else if (tourLocation.toLowerCase().includes('outcall')) {
          incallOutcallValue = 'Outcall';
        }
      }
      
      setFormData(prev => ({
        ...prev,
        location: tourLocation || prev.location,
        incallOutcall: incallOutcallValue || prev.incallOutcall,
        preferences: preferencesText || prev.preferences
      }));
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${API}/bookings`, formData);
      setStatus({ 
        type: 'success', 
        message: '✓ Your booking enquiry has been received! I\'ll respond within 24 hours to confirm availability.\n\n📧 Please check your spam/junk folder to ensure you don\'t miss my response.' 
      });
      setFormData({
        firstName: '',
        surname: '',
        phone: '',
        email: '',
        date: '',
        time: '',
        duration: '',
        location: '',
        incallOutcall: '',
        preferences: ''
      });
    } catch (error) {
      setStatus({ 
        type: 'error', 
        message: 'There was an error submitting your enquiry. Please try again or email me directly.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Page Header with Image */}
      <section style={{ padding: '80px 40px 60px' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div className="grid-two-column" style={{ gap: '60px', alignItems: 'start' }}>
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_intimate-editorial/artifacts/mrld0ov5_Honey%20Sofia%20-%200098-Edit-face%20blur.jpg"
                  alt="Honey Sofia"
                  style={{ 
                    width: '100%', 
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </div>
              
              <div>
                <h1 className="hero-medium" style={{ marginBottom: '24px' }}>
                  I Can't Wait to Meet You
                </h1>
                <p className="body-large" style={{ marginBottom: '20px', lineHeight: '1.8' }}>
                  I love a little time to anticipate our date — imagining your smile, wondering what we'll talk about, building that delicious sense of excitement. Please reach out with at least 48 hours' notice when possible.
                </p>
                <p className="body-regular" style={{ marginBottom: '24px', lineHeight: '1.7', color: 'var(--text-secondary)' }}>
                  You can reach me at <a href="mailto:contacthoneysofia@gmail.com" style={{ color: 'var(--burgundy)', textDecoration: 'underline' }}>contacthoneysofia@gmail.com</a>, text me at <a href="sms:0401627005" style={{ color: 'var(--burgundy)', textDecoration: 'underline' }}>0401 627 005</a> for short notice bookings, or fill out the form below:
                </p>
                
                <ul style={{ 
                  listStyle: 'none', 
                  padding: '0',
                  marginBottom: '24px'
                }}>
                  {[
                    { id: 'booking-info-1', text: 'Your name' },
                    { id: 'booking-info-2', text: 'Preferred date & time' },
                    { id: 'booking-info-3', text: 'Duration' },
                    { id: 'booking-info-4', text: 'Location (incall/outcall)' },
                    { id: 'booking-info-5', text: 'Any preferences or questions' }
                  ].map((item) => (
                    <li 
                      key={item.id}
                      className="body-regular"
                      style={{ 
                        marginBottom: '8px',
                        paddingLeft: '20px',
                        position: 'relative',
                        color: 'var(--text-secondary)'
                      }}
                    >
                      <span style={{ 
                        position: 'absolute', 
                        left: '0', 
                        color: 'var(--burgundy)',
                        fontSize: '18px'
                      }}>•</span>
                      {item.text}
                    </li>
                  ))}
                </ul>

                <div style={{ 
                  padding: '24px 28px',
                  background: 'rgba(90, 30, 43, 0.05)',
                  borderLeft: '3px solid var(--burgundy)',
                  marginTop: '32px'
                }}>
                  <p className="body-regular" style={{ marginBottom: '16px', lineHeight: '1.7', fontWeight: '500' }}>
                    All bookings require a 30% non-refundable deposit. You can reschedule once with 48 hours' notice.
                  </p>
                  <p className="body-small" style={{ margin: 0, lineHeight: '1.6' }}>
                    <strong>Beem: @honeysofia</strong> — My preferred deposit method as it's the most discreet. It's a free, instant, anonymous app supported by the big four Australian banks. If Beem isn't an option for you, I also offer <strong>USDT (tron)</strong>, gift card, and international-friendly options.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Decorative Quote Section */}
      <section style={{ 
        padding: '80px 40px', 
        background: 'var(--bg-subtle)',
        textAlign: 'center'
      }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <p className="hero-medium" style={{ 
              fontStyle: 'italic', 
              marginBottom: '24px',
              color: 'var(--text-primary)'
            }}>
              "I'm drawn to people who appreciate thoughtful connection and aren't afraid to ask for what they want."
            </p>
            <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              — Honey Sofia
            </p>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section style={{ padding: '40px 40px 120px', background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <h2 className="heading-2" style={{ marginBottom: '32px', textAlign: 'center' }}>
              Booking Form
            </h2>

            {status.message && (
              <div style={{
                padding: '20px 28px',
                marginBottom: '32px',
                background: status.type === 'success' ? 'rgba(90, 30, 43, 0.1)' : 'rgba(255, 0, 0, 0.1)',
                border: `1px solid ${status.type === 'success' ? 'var(--burgundy)' : '#ff0000'}`,
                borderRadius: '0px'
              }}>
                <p className="body-regular" style={{ 
                  color: status.type === 'success' ? 'var(--burgundy)' : '#cc0000',
                  margin: 0,
                  whiteSpace: 'pre-line',
                  lineHeight: '1.7'
                }}>
                  {status.message}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="firstName" className="form-label">First Name (Legal Name) *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="surname" className="form-label">Surname (Legal Name) *</label>
                <input
                  type="text"
                  id="surname"
                  name="surname"
                  value={formData.surname}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  Phone Number * 
                  <span style={{ 
                    color: 'var(--burgundy)', 
                    fontWeight: '600', 
                    marginLeft: '8px',
                    fontSize: '14px'
                  }}>
                    (Required for screening)
                  </span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="+61 XXX XXX XXX"
                  required
                />
                <p style={{ 
                  fontSize: '13px', 
                  color: 'var(--text-secondary)', 
                  marginTop: '6px',
                  fontStyle: 'italic'
                }}>
                  Your phone number is required for screening purposes to ensure our mutual safety and comfort.
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div className="form-group">
                  <label htmlFor="date" className="form-label">Preferred Date</label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className="form-input"
                    required
                    style={{ width: '100%' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="time" className="form-label">Preferred Time</label>
                  <input
                    type="time"
                    id="time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    className="form-input"
                    required
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="duration" className="form-label">Length (Duration) *</label>
                <select
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  className="form-input"
                  required
                >
                  <option value="">Select duration...</option>
                  <option value="1 hour">1 hour</option>
                  <option value="2 hours">2 hours</option>
                  <option value="3 hours">3 hours</option>
                  <option value="4 hours">4 hours</option>
                  <option value="Dinner date">Dinner date</option>
                  <option value="Overnight">Overnight</option>
                  <option value="Extended">Extended (Multiple days)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="incallOutcall" className="form-label">Incall/Outcall *</label>
                <select
                  id="incallOutcall"
                  name="incallOutcall"
                  value={formData.incallOutcall}
                  onChange={handleChange}
                  className="form-input"
                  required
                >
                  <option value="">Select...</option>
                  <option value="Incall">Incall</option>
                  <option value="Outcall">Outcall</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="location" className="form-label">Location *</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="e.g., Sydney CBD, Canberra, Melbourne"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="preferences" className="form-label">Preferences & Additional Information (Optional)</label>
                <textarea
                  id="preferences"
                  name="preferences"
                  value={formData.preferences}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="Tell me a bit about yourself and what experience you're interested in..."
                />
              </div>

              <button 
                type="submit" 
                className="btn-primary" 
                disabled={isSubmitting}
                style={{ 
                  width: '100%',
                  opacity: isSubmitting ? 0.6 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmitting ? 'Sending Enquiry...' : 'Submit Booking Enquiry'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Bookings;
